export type FaqItem = {
  question: string;
  answer: string;
};

export const faqItems: FaqItem[] = [
  {
    question: "What is Equity Replay?",
    answer: "Equity Replay is an educational historical portfolio simulator. Add stocks and allocation weights, then see how that mix would have moved across a selected historical window using available closing-price data.",
  },
  {
    question: "Does Equity Replay place trades or manage money?",
    answer: "No. Equity Replay is not a brokerage, financial institution, or investment adviser. It does not place orders, hold assets, execute a buy-and-hold strategy, or rebalance a portfolio.",
  },
  {
    question: "How are portfolio results calculated?",
    answer: "Each position is rebased to 100 at the start of the selected window. The weights are applied once at that starting point, then the rebased closing-price series are combined into one illustrative portfolio line. Fees, taxes, dividends, deposits, withdrawals, slippage, and interim trades are not modeled.",
  },
  {
    question: "What does “weights applied once” mean?",
    answer: "The allocation is a fixed starting mix for the replay. The app does not periodically restore the percentages as prices move, so the result is not a rebalancing simulation.",
  },
  {
    question: "Why can a stock be unavailable for a long window?",
    answer: "Some symbols do not have enough available closing-price history for the selected dates. Recently listed stocks, renamed symbols, ETFs with incomplete data, and certain securities may only support shorter windows.",
  },
  {
    question: "Are the investor and public-figure portfolios verified personal accounts?",
    answer: "They are normalized public-disclosure or allocation examples, not complete personal account statements. Each example is labeled with its source, date, and assumptions where available. A public-figure example should not be read as a recommendation or a claim about a private account.",
  },
  {
    question: "Can I create and save my own portfolio?",
    answer: "Yes. You can build a custom mix, adjust the weights, choose a historical window, and review the replay. Sign in to save portfolios and return to them later.",
  },
  {
    question: "What is included in Replay Pro?",
    answer: "Replay Pro increases the number of portfolios you can save and the number of AI mix drafts you can create each month. The current limits and price are shown on the pricing page before checkout.",
  },
  {
    question: "How does billing work?",
    answer: "Replay Pro is billed through Stripe on the monthly or annual schedule selected at checkout. Your plan is updated after Stripe confirms the subscription event. You can manage payment details and cancellation from the billing portal in Account.",
  },
  {
    question: "Can I cancel Replay Pro?",
    answer: "Yes. Open Account and choose Manage billing to use Stripe’s billing portal. Cancellation affects future renewal; access remains governed by the subscription status and period shown in your account.",
  },
  {
    question: "Is Equity Replay financial advice?",
    answer: "No. Historical movement is not a promise or prediction of future results. Equity Replay is for exploration and education only, and it should not replace advice from a qualified professional.",
  },
  {
    question: "How can I contact Equity Replay?",
    answer: "For product, billing, privacy, or account questions, email team@equityreplay.com. Do not send payment card numbers or other sensitive information by email.",
  },
];

const homeFaqQuestions = new Set([
  "What is Equity Replay?",
  "How are portfolio results calculated?",
  "Are the investor and public-figure portfolios verified personal accounts?",
  "Is Equity Replay financial advice?",
]);

export const homeFaqItems = faqItems.filter((item) => homeFaqQuestions.has(item.question));

export function faqStructuredData(url: string, items: FaqItem[] = faqItems) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name: "Equity Replay frequently asked questions",
    description: "Get clear answers about historical portfolio replays, public portfolios, stock and ETF data, Replay Pro, accounts, and calculation methods.",
    url,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}
